/**
 * In-memory request cache com TTL e deduplicação de requisições em voo.
 *
 * Objetivo: evitar chamadas repetidas a endpoints estáveis do cotista
 * (cotas, empreendimentos, semanas, boletos) cada vez que o usuário
 * troca de aba — os dados são carregados uma vez e reutilizados.
 *
 * Regras:
 *  - Promise em voo: retorna a mesma Promise (sem segunda requisição paralela).
 *  - Dentro do TTL: retorna o resultado em cache.
 *  - Erro: entrada removida → próxima chamada refaz a requisição.
 *  - Após escrita (POST/PATCH/DELETE): chamar `invalidate(prefixo)`.
 */
import { api } from "./api";

interface Entry {
  promise: Promise<unknown>;
  resolvedAt: number | null;
  ttlMs: number;
}

const store = new Map<string, Entry>();

/** GET com cache. Retorna dado em cache se dentro do TTL; caso contrário busca. */
export function cachedGet<T>(url: string, ttlMs = 10 * 60 * 1000): Promise<T> {
  const hit = store.get(url) as Entry | undefined;

  if (hit) {
    if (hit.resolvedAt === null) return hit.promise as Promise<T>;       // em voo
    if (Date.now() - hit.resolvedAt < hit.ttlMs) return hit.promise as Promise<T>; // válido
  }

  const promise = (api.get<T>(url) as Promise<unknown>).catch((err) => {
    store.delete(url); // falha não fica em cache — próxima chamada tenta de novo
    throw err;
  });

  const entry: Entry = { promise, resolvedAt: null, ttlMs };
  store.set(url, entry);
  (promise as Promise<T>).then(() => { entry.resolvedAt = Date.now(); }).catch(() => {});

  return promise as Promise<T>;
}

/**
 * Remove do cache todas as entradas cujo URL começa com `prefix`.
 * Chamar após operações de escrita que invalidem os dados.
 * Ex.: invalidate(`/api/portal/cotas/${id}/reservas/`)
 */
export function invalidate(prefix: string): void {
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) store.delete(key);
  }
}

/** Faz a requisição em background (fire-and-forget); ignora erros. */
export function prefetch(url: string, ttlMs?: number): void {
  cachedGet(url, ttlMs).catch(() => {});
}

/** Limpa todo o cache (usar no logout). */
export function clearAll(): void {
  store.clear();
}
